import React, { ReactNode, useCallback, useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { Option } from '../../../ana-toolbox'
import { defer } from '../../utils/helpers/defer'
import { updateOrRemoveAtIndex } from '../../utils/helpers/updateOrRemoveAtIndex'
import { Checkbox } from '../../utils/shared/Checkbox'
import { FormControlLabel } from '../../utils/shared/FormControlLabel'
import { Translate } from '../translate/translate'

import * as S from './checklist-input.styles'

export type ChecklistInputProps = {
  label?: ReactNode
  selectedValues?: string[]
  options: Option[]
  className?: string
  error?: string
  required?: boolean
  onChange: (values: string[]) => void
  allowCustomOptions?: boolean
}

const emptyOption: Option = {
  value: '',
  label: '',
}

export const ChecklistInput = ({
  label,
  selectedValues: externalSelectedValues,
  options,
  className,
  error,
  required,
  onChange,
  allowCustomOptions,
}: ChecklistInputProps) => {
  const { t } = useTranslation()
  const customOptionInputRef = useRef<HTMLInputElement | null>(null)
  const [innerSelectedValues, setInnerSelectedValues] = useState<string[]>([])

  const selectedValues = useMemo(
    () => externalSelectedValues ?? innerSelectedValues,
    [externalSelectedValues, innerSelectedValues]
  )

  const optionsByValue = useMemo(() => Object.fromEntries(options.map((option) => [option.value, option])), [options])

  const getInitialCustomOptions = () => {
    const customOptions = selectedValues
      .filter(
        (value) =>
          // Get the options that are not available in optionsByValue. When it's not there it
          // means that the value is a custom option created by the user.
          !optionsByValue[value]
      )
      .map((value) => ({ value, label: value }))

    return [...customOptions, emptyOption]
  }

  const [customOptions, setCustomOptions] = useState<Option[]>(getInitialCustomOptions)

  const selectedValuesLookup = useMemo(
    () => Object.fromEntries(selectedValues.map((value) => [value, true])),
    [selectedValues]
  )

  // This function returns the index of a given option index on the `selectedValues` array.
  const getCustomOptionIndexAtSelectedValues = useCallback(
    (optionIndex: number) => {
      // Subtracting 1 because the `customOptions` always have an empty option at the end of the list
      const amountOfCustomOptions = customOptions.length - 1

      return selectedValues.length - amountOfCustomOptions + optionIndex
    },
    [selectedValues.length, customOptions.length]
  )

  const handleChange = useCallback(
    (values: string[]) => {
      if (!externalSelectedValues) setInnerSelectedValues(values)

      onChange(values)
    },
    [onChange, externalSelectedValues]
  )

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const fieldName = e.target.name

    const isCustom = !optionsByValue[fieldName]

    if (checked) {
      // We add the custom values to the end of the list and the pre-defined ones to the beginning of
      // the list in order to reduce the complexity to find a custom value in the list of selected values.
      handleChange(isCustom ? [...selectedValues, fieldName] : [fieldName, ...selectedValues])
    } else {
      handleChange(selectedValues.filter((value) => value !== fieldName))
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      customOptionInputRef.current?.focus()
    }
  }

  const createHandleCustomInputChange = useCallback(
    (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value
      const isEmpty = e.target.value === ''
      const isNew = customOptions.length === index + 1

      const customOptionIndexAtSelectedValues = getCustomOptionIndexAtSelectedValues(index)
      const newValue = { label: inputValue, value: inputValue }

      const newValues = isNew
        ? [...selectedValues, inputValue]
        : updateOrRemoveAtIndex(selectedValues, customOptionIndexAtSelectedValues, newValue.value, isEmpty)

      const updatedOptions = updateOrRemoveAtIndex(customOptions, index, newValue, isEmpty)

      const newCustomOptions = isNew ? [...updatedOptions, emptyOption] : updatedOptions

      setCustomOptions(newCustomOptions)
      handleChange(newValues)

      if (isEmpty && customOptionInputRef.current) {
        // Deferring the focus because the custom input will change as soon as the empty option
        // is removed, causing the custom option input key change.
        defer(() => {
          customOptionInputRef.current?.focus()
        })
      }
    },
    [handleChange, customOptions, selectedValues, getCustomOptionIndexAtSelectedValues]
  )

  return (
    <>
      <S.FormControl className={className} error={Boolean(error)} fullWidth>
        {label && (
          <S.FormLabel error={Boolean(error)} required={required}>
            <Translate>{label}</Translate>
          </S.FormLabel>
        )}

        <div>
          {options.map((option) => (
            <S.CheckboxContainer key={option.value}>
              <FormControlLabel
                label={<Translate>{option.label}</Translate>}
                control={
                  <Checkbox
                    name={option.value}
                    inputProps={{ name: option.value }}
                    checked={selectedValuesLookup[option.value] || false}
                    onChange={handleCheckboxChange}
                  />
                }
                labelPlacement="end"
              />
            </S.CheckboxContainer>
          ))}
        </div>
      </S.FormControl>

      <div>
        {allowCustomOptions &&
          customOptions.map((option, index) => {
            const isLast = index === customOptions.length - 1

            return (
              <S.InputContainer key={`checklist-input-${index}`}>
                {!isLast && (
                  <Checkbox
                    name={option.value}
                    checked={selectedValuesLookup[option.value] || false}
                    onChange={handleCheckboxChange}
                  />
                )}

                <S.Input
                  ref={customOptionInputRef}
                  value={option.value}
                  onChange={createHandleCustomInputChange(index)}
                  onKeyPress={handleKeyPress}
                  placeholder={isLast ? t('other_option', { postProcess: [] }) || '' : undefined}
                />
              </S.InputContainer>
            )
          })}
      </div>
    </>
  )
}
